import { useEffect, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { ulid } from 'ulid';
import { useLocation } from 'react-router-dom';

import { Card } from '@/components/ui/card';

import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useGo } from '@/hooks/useResourceNavigation';
import { FormPageLayout } from '@/components/layout/form-page-layout';
import type {
	CreateTariffMutation,
	CreateTariffMutationVariables,
	TariffTypeEnum
} from '@/graphql/generated';
import { extractCheckResultError } from '@/lib/checkResult';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';

import { TariffForm } from './tariff-form';
import {
	buildCreateTariffInput,
	type TariffFormSubmitData
} from './tariff-input';
import {
	TARIFF_CREATE_MUTATION
} from './tariffs.gql.tsx';

interface CloneStepData {
	tariffStepId?: string;
	stepSequence?: number;
	intervalMinutes: number;
	intervalCharge: number;
	maxIntervals?: number | null;
}

interface CloneData {
	name?: string;
	tariffTypeEnum?: TariffTypeEnum;
	flatRateAmount?: number | null;
	tariffSteps?: CloneStepData[];
}

interface LocationState {
	cloneData?: CloneData;
}

export const TariffsCreate = () => {
	const { setPageHeader, resetPageHeader } = usePageHeader();
	const go = useGo();
	const location = useLocation();
	const cloneData = (location.state as LocationState | null)?.cloneData;

	const idempotentTariffId = useMemo(() => ulid(), []);

	useEffect(() => {
		setPageHeader({
			title: cloneData ? 'Clone tariff' : 'Create tariff',
			description: cloneData
				? 'Create a new tariff based on an existing one'
				: 'Define a new pricing tariff',
			showBackButton: true,
			onBackClick: () => go({ to: '/tariffs' })
		});

		return () => {
			resetPageHeader();
		};
	}, [cloneData, go, resetPageHeader, setPageHeader]);

	// No need to fetch tariff types anymore since they are enum values

	const [createTariff, { loading: isSubmitting }] = useMutation<
		CreateTariffMutation,
		CreateTariffMutationVariables
	>(TARIFF_CREATE_MUTATION);

	const handleSubmit = async (formData: TariffFormSubmitData) => {
		const payload = buildCreateTariffInput(formData, idempotentTariffId);

		try {
			const { data } = await createTariff(
				getMutationOptions('tariffs', { input: payload })
			);

			const message = extractCheckResultError(data?.createTariff);

			if (message) {
				showMutationErrorToast('tariffs', 'create', { message });
				return;
			}

			showMutationSuccessToast('tariffs', 'create');
			go({ to: `/tariffs?success=${idempotentTariffId}&mode=create` });
		} catch (error) {
			showMutationErrorToast('tariffs', 'create', { error });
		}
	};

	return (
		<FormPageLayout>
			<Card>
				<TariffForm
					mode={cloneData ? 'clone' : 'create'}
					initialData={cloneData ? {
						name: cloneData.name ?? '',
						tariffTypeEnum: cloneData.tariffTypeEnum,
						flatRateAmount: cloneData.flatRateAmount ?? undefined,
						tariffSteps: cloneData.tariffSteps?.map(step => ({
							// Omit tariffStepId so new ones are generated on creation
							stepSequence: step.stepSequence,
							intervalMinutes: step.intervalMinutes,
							intervalCharge: step.intervalCharge,
							maxIntervals: step.maxIntervals ?? undefined
						})) ?? []
					} as any : undefined}
					onSubmit={handleSubmit}
					isSubmitting={isSubmitting}
					headerTitle={cloneData ? 'Clone Tariff' : 'Create Tariff'}
				/>
			</Card>
		</FormPageLayout>
	);
};
